import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ShieldAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePortalRole } from "@/hooks/usePortalRole";

const STAFF_ROLES = new Set([
  "admin",
  "marketing_manager",
  "customer_service",
  "sales_manager",
  "consultant",
  "installer",
  "surveyor",
]);

const DISMISS_KEY = "evo_mfa_banner_dismissed_v1";

/**
 * Non-blocking banner shown to staff/admins who have not yet enrolled a TOTP factor.
 * Dismissible per browser (localStorage). Enforcement comes in Phase 2.
 */
export default function StaffMfaBanner() {
  const { roles, session } = usePortalRole();
  const [needsMfa, setNeedsMfa] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  const isStaff = roles.some(r => r && STAFF_ROLES.has(r));

  useEffect(() => {
    if (!session || !isStaff) { setNeedsMfa(false); return; }
    let cancelled = false;
    supabase.auth.mfa.listFactors().then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setNeedsMfa(false); return; }
      const verified = (data?.totp ?? []).filter((f: any) => f.status === "verified");
      setNeedsMfa(verified.length === 0);
    });
    return () => { cancelled = true; };
  }, [session, isStaff]);

  if (!isStaff || !needsMfa || dismissed) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/40">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <ShieldAlert className="w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0" />
        <div className="flex-1 text-sm text-amber-900 dark:text-amber-200">
          <span className="font-medium">Two-step verification recommended.</span>{" "}
          <span className="hidden sm:inline">Add an authenticator app to protect your staff account. Required for all staff before our wider launch.</span>
        </div>
        <Button asChild size="sm" variant="default" className="shrink-0 h-7">
          <Link to="/account/security">Set up</Link>
        </Button>
        <button
          aria-label="Dismiss"
          className="p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 shrink-0"
          onClick={() => { try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* storage unavailable: dismiss for this view only */ } setDismissed(true); }}
        >
          <X className="w-4 h-4 text-amber-700 dark:text-amber-400" />
        </button>
      </div>
    </div>
  );
}
